import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Mail, Lock, Loader2, Info } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ThemeToggle } from "@/components/ThemeToggle";
import { BrandLogo } from "@/components/BrandLogo";
import StatusBar from "@/components/StatusBar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { toast } from "sonner";

const ACCESS_REQUEST_URL = "https://unitedcloud.ca/conduit-2-access-request";

// Iter 101 Phase 2 — Same-origin `next` param honoring for OIDC flow
// resumption. When Conduit's `/api/oauth/authorize` bounces an
// unauthenticated request through `/login?next=…`, we send the user to
// the target URL after login. Absolute cross-origin URLs are rejected
// so this never doubles as an open redirect.
function safeNext(rawNext) {
  if (!rawNext || typeof rawNext !== "string") return null;
  // Only same-origin absolute paths (must start with `/` and cannot
  // start with `//` — that would be protocol-relative to another host).
  if (!rawNext.startsWith("/") || rawNext.startsWith("//")) return null;
  return rawNext;
}

export default function Login() {
  const { user, setUser, loading } = useAuth();
  const navigate = useNavigate();
  const [search] = useSearchParams();
  const nextParam = safeNext(search.get("next"));
  // Iter 120 — Generic 401 notice. `reason=signin` is set when a protected
  // deep-link bounced here; `reason=expired` when a live session went stale.
  const reason = search.get("reason");
  const notice = reason === "expired"
    ? "Your session has expired. Please sign in again to continue."
    : reason === "signin"
      ? "Please sign in to open that page."
      : "";
  const brandPreview = !!search.get("brand_preview");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [forgotOpen, setForgotOpen] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [mfaToken, setMfaToken] = useState("");
  const [mfaCode, setMfaCode] = useState("");

  const afterLogin = () => {
    // Guard against double-navigate: the mount-time useEffect also
    // routes to `nextParam` once `user` becomes truthy, so make every
    // path idempotent.
    setAutoRedirected(true);
    if (nextParam) {
      if (nextParam.startsWith("/api/")) {
        window.location.assign(nextParam);
      } else {
        navigate(nextParam, { replace: true });
      }
      return;
    }
    navigate("/dashboard", { replace: true });
  };

  const [autoRedirected, setAutoRedirected] = useState(false);
  const [msEnabled, setMsEnabled] = useState(false);

  useEffect(() => {
    api.get("/auth/microsoft/config").then((r) => setMsEnabled(!!r.data?.enabled)).catch(() => {});
    // Microsoft SSO can hand back an MFA challenge, or an error to surface.
    const q = new URLSearchParams(window.location.search);
    const msMfa = q.get("ms_mfa");
    if (msMfa) setMfaToken(msMfa);
    const err = q.get("sso_error");
    if (err) setError(`Microsoft sign-in failed: ${err.replace(/_/g, " ")}`);
    // Iter 241 — Google sign-in refusals (e.g. no Conduit account) arrive here
    // with the backend's message instead of a silent bounce.
    const gErr = q.get("google_error");
    if (gErr) setError(gErr);
  }, []);

  useEffect(() => {
    const pending = sessionStorage.getItem("pending_mfa_token");
    if (pending) {
      sessionStorage.removeItem("pending_mfa_token");
      setMfaToken(pending);
    }
  }, []);

  // Iter 102 — Only auto-redirect when the user is ALREADY authenticated
  // on mount (e.g. hitting /login while still holding a valid session
  // cookie, or bouncing back from OAuth). Post-login submits handle
  // their own navigation via `afterLogin()` — folding both paths into
  // one effect used to race with the explicit call and occasionally
  // land users on /dashboard instead of the intended `?next=…` target.
  useEffect(() => {
    if (loading || autoRedirected) return;
    // Iter 119ab — `?brand_preview=` renders this page purely as a visual
    // preview for an admin who is, by definition, already signed in. Bouncing
    // them to /dashboard would make the preview impossible to see.
    if (brandPreview) return;
    if (user) {
      afterLogin();
    }
  }, [loading, user, autoRedirected, brandPreview]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      if (data.mfa_required) {
        setMfaToken(data.mfa_token);
      } else {
        setUser(data);
        afterLogin();
      }
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleMfaVerify = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      const { data } = await api.post("/auth/mfa/verify", { mfa_token: mfaToken, code: mfaCode });
      setUser(data);
      afterLogin();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    // Iter 101 — Preserve `next` through the Emergent Google Auth round-trip
    // so an OIDC-triggered login via Google still lands on /api/oauth/authorize.
    const target = nextParam ? `/dashboard#next=${encodeURIComponent(nextParam)}` : "/dashboard";
    const redirectUrl = window.location.origin + target;
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleMicrosoft = () => {
    const returnTo = nextParam || "/dashboard";
    window.location.href =
      `${process.env.REACT_APP_BACKEND_URL}/api/auth/microsoft/login?return_to=${encodeURIComponent(returnTo)}`;
  };

  const handleForgot = async () => {
    try {
      await api.post("/auth/forgot-password", { email: forgotEmail });
      toast.success("If the account exists, a reset link was generated (check server logs).");
      setForgotOpen(false);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const fill = () => {};

  return (
    <div className="flex min-h-screen flex-col bg-background">
      {brandPreview && (
        <div data-testid="login-brand-preview-ribbon"
          className="bg-amber-500 px-4 py-1.5 text-center text-xs font-semibold text-white">
          Branding preview — this is how your sign-in page will look. Nothing has been saved.
        </div>
      )}
      <StatusBar />
      <div className="grid flex-1">
      {/* Left: form */}
      <div className="flex items-center justify-center p-6 sm:p-10">
        <div className="w-full max-w-sm">
          <div className="mb-8 flex items-center justify-between">
            <div className="flex items-center" data-testid="brand-logo-login">
              <BrandLogo className="h-10" />
            </div>
            <ThemeToggle />
          </div>

          {mfaToken ? (
            <>
              <h1 className="font-heading text-2xl font-bold text-slate-900">Two-factor verification</h1>
              <p className="mt-1 text-sm text-slate-500">Enter the 6-digit code from your authenticator app, or a backup code.</p>
              <form onSubmit={handleMfaVerify} className="mt-6 space-y-4">
                <div className="space-y-1.5">
                  <Label htmlFor="mfa-login-code">Verification code</Label>
                  <Input id="mfa-login-code" data-testid="mfa-login-code-input" autoFocus value={mfaCode}
                    onChange={(e) => setMfaCode(e.target.value)} placeholder="000000"
                    className="text-center tracking-[0.4em]" />
                </div>
                {error && (
                  <div data-testid="login-error" className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>
                )}
                <Button type="submit" data-testid="mfa-verify-button" disabled={submitting || !mfaCode}
                  className="w-full bg-primary hover:bg-primary/90 text-primary-foreground">
                  {submitting && <Loader2 className="h-4 w-4 animate-spin" />} Verify
                </Button>
                <button type="button" data-testid="mfa-back-button"
                  onClick={() => { setMfaToken(""); setMfaCode(""); setError(""); }}
                  className="w-full text-center text-xs text-slate-400 hover:text-primary">Back to sign in</button>
              </form>
            </>
          ) : (
          <>
          <h1 className="font-heading text-2xl font-bold text-slate-900">Welcome back</h1>
          <p className="mt-1 text-sm text-slate-500">Sign in to your control center.</p>

          {notice && (
            <div data-testid="login-auth-notice"
              className="mt-4 rounded-md border border-sky-200 bg-sky-50 px-3 py-2 text-sm text-sky-800">
              {notice}
            </div>
          )}

          <form onSubmit={handleLogin} className="mt-6 space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="email">Email</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input id="email" data-testid="login-email-input" type="email" required value={email}
                  onChange={(e) => setEmail(e.target.value)} placeholder="you@iplogin.io" className="pl-9" />
              </div>
            </div>
            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <Label htmlFor="password">Password</Label>
                <button type="button" data-testid="forgot-password-link" onClick={() => setForgotOpen(true)}
                  className="text-xs text-primary hover:underline">Forgot password?</button>
              </div>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input id="password" data-testid="login-password-input" type="password" required value={password}
                  onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" className="pl-9" />
              </div>
            </div>

            {error && (
              <div data-testid="login-error" className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>
            )}

            <Button type="submit" data-testid="login-submit-button" disabled={submitting}
              className="w-full bg-primary hover:bg-primary/90 text-primary-foreground">
              {submitting && <Loader2 className="h-4 w-4 animate-spin" />} Sign in
            </Button>
          </form>

          <div className="my-5 flex items-center gap-3">
            <div className="h-px flex-1 bg-slate-200" />
            <span className="text-xs text-slate-400">OR</span>
            <div className="h-px flex-1 bg-slate-200" />
          </div>

          <Button type="button" variant="outline" data-testid="google-login-button" onClick={handleGoogle}
            className="w-full border-slate-300">
            <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" alt="" className="h-4 w-4" />
            Continue with Google
          </Button>

          {msEnabled && (
            <Button type="button" variant="outline" data-testid="microsoft-login-button" onClick={handleMicrosoft}
              className="mt-2 w-full border-slate-300">
              <svg aria-hidden="true" className="h-4 w-4" viewBox="0 0 23 23">
                <path fill="#f35325" d="M1 1h10v10H1z" /><path fill="#81bc06" d="M12 1h10v10H12z" />
                <path fill="#05a6f0" d="M1 12h10v10H1z" /><path fill="#ffba08" d="M12 12h10v10H12z" />
              </svg>
              Continue with Microsoft
            </Button>
          )}

          <div className="mt-4 flex items-center justify-center gap-1.5">
            <a href={ACCESS_REQUEST_URL} data-testid="request-access-link"
              className="text-sm font-medium text-primary hover:underline">
              Request access
            </a>
            <Popover>
              <PopoverTrigger asChild>
                <button type="button" aria-label="What is this?" data-testid="request-access-info"
                  className="rounded-full p-0.5 text-slate-400 transition-colors hover:text-primary">
                  <Info className="h-3.5 w-3.5" />
                </button>
              </PopoverTrigger>
              <PopoverContent align="center" className="w-72 text-xs leading-relaxed"
                data-testid="request-access-info-popover">
                Access to Conduit 2 is restricted to UnitedCloud partners and must be approved by the
                UnitedCloud Partner Team. Send a request and we&apos;ll be in touch once it&apos;s reviewed.
              </PopoverContent>
            </Popover>
          </div>

          {/* Iter 80/86: Test-persona quick-login grid — off by default
              (was leaking on public preview URLs). Enable with the
              `?dev=1` query param, e.g. /login?dev=1. Bookmark the
              URL for staging use. Passwords still live in the client
              bundle — move server-side before public launch. */}
          {typeof window !== "undefined"
              && new URLSearchParams(window.location.search).get("dev") === "1"
              && /preview\.emergentagent/i.test(window.location.hostname) && (
            <div className="mt-6 rounded-md border border-dashed border-amber-300 bg-amber-50 p-3"
                 data-testid="test-persona-grid">
              <div className="mb-2 flex items-center gap-2 text-[11px] font-bold uppercase tracking-wider text-amber-800">
                Test personas <span className="rounded-full bg-amber-200 px-1.5 py-0.5 text-[9px]">preview only</span>
              </div>
              <div className="grid grid-cols-2 gap-1.5 sm:grid-cols-3">
                {[
                  { label: "Super Admin",  email: "superadmin@unitedcloud.ca",     pw: "UC!ikdfDnL9g633r3ZY#26" },
                  { label: "Partner Success", email: "partnersuccess@unitedcloud.ca", pw: "UC!Lcy9R73wEZzw0qP5#26" },
                  { label: "Partner Admin", email: "partneradmin@unitedcloud.ca",  pw: "UC!2SicgDW9ye5lT2LB#26" },
                  { label: "Partner",       email: "partner@unitedcloud.ca",       pw: "UC!9cljJXToLi64taQH#26" },
                  { label: "Customer",      email: "customer@unitedcloud.ca",      pw: "UC!75KbeVt94Xs3cgnb#26" },
                  { label: "Engineering",   email: "engineering@unitedcloud.ca",   pw: "UC!AeSzPJWZ08VJ8ow6#26" },
                  { label: "HR",            email: "hr@unitedcloud.ca",            pw: "UC!jCqRcrV6an2P5ccO#26" },
                  { label: "IT",            email: "it@unitedcloud.ca",            pw: "UC!v75DdOGEqT499Buz#26" },
                  { label: "Inventory",     email: "inventory@unitedcloud.ca",     pw: "UC!1ZJF98wo4tWZLpIr#26" },
                  { label: "LNP",           email: "lnp@unitedcloud.ca",           pw: "UC!4w0KRu9ocFG3rsgd#26" },
                  { label: "Project",       email: "project@unitedcloud.ca",       pw: "UC!tSzeWGdhYMwI4KSc#26" },
                  { label: "TechOps",       email: "techops@unitedcloud.ca",       pw: "UC!ZGYqjMmbIabIX8dU#26" },
                ].map((p) => (
                  <button key={p.email} type="button"
                          data-testid={`persona-${p.email}`}
                          onClick={() => { setEmail(p.email); setPassword(p.pw); }}
                          title={`${p.email} — click to fill, then Sign in`}
                          className="rounded-md border border-slate-200 bg-white px-2 py-1 text-[11px] font-medium text-slate-700 hover:bg-slate-50 hover:border-primary transition">
                    {p.label}
                  </button>
                ))}
              </div>
            </div>
          )}
          </>
          )}
        </div>
      </div>

      </div>

      <Dialog open={forgotOpen} onOpenChange={setForgotOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reset password</DialogTitle>
            <DialogDescription>Enter your email and we&apos;ll generate a reset link.</DialogDescription>
          </DialogHeader>
          <div className="space-y-1.5">
            <Label htmlFor="forgot-email">Email</Label>
            <Input id="forgot-email" data-testid="forgot-email-input" type="email" value={forgotEmail}
              onChange={(e) => setForgotEmail(e.target.value)} placeholder="you@iplogin.io" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setForgotOpen(false)}>Cancel</Button>
            <Button data-testid="forgot-submit-button" onClick={handleForgot} className="bg-primary hover:bg-primary/90 text-primary-foreground">Send reset link</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
